import { z } from "zod";

/**
 * Shared DTO — chia suất chi phí (US-E14.F1-0170 · @UC9190). MỘT nguồn sự thật cho enum splitRule
 * + participant type, dùng chung api ↔ mobile. @gate money 🟠.
 *
 * BẤT BIẾN (invariants):
 * - B1: tiền = `shareMinor` STRING minor-units (BigInt an toàn qua JSON) — regex chỉ chữ số, no-float.
 * - `weight` = INPUT tính tỉ trọng (number) — KHÔNG phải tiền.
 * - XOR chủ thể: đúng MỘT trong `groupMemberId` / `dependentId` (mirror CHECK DB
 *   `expense_participants_subject_xor_check`).
 * - `.strict()` — reject field lạ (chống over-post).
 * - SPLIT_RULES là nguồn sự thật đơn (mirror CHECK DB `expenses_split_rule_check`); int-spec S03 AC7
 *   drift-guard: nếu enum ↔ whitelist DB lệch → test đỏ.
 */

/**
 * Quy tắc chia suất chi. Nguồn sự thật đơn (mirror `packages/domain` TX_TYPES style).
 * KHỚP TUYỆT ĐỐI whitelist CHECK DB `expenses_split_rule_check` (migration 20260711170000).
 *  - equal:           chia đều số người.
 *  - ratio:           theo tỉ lệ weight (phần trăm/hệ số).
 *  - shares:          theo số suất nguyên (weight = số suất).
 *  - by_night:        theo số đêm (du lịch/lưu trú).
 *  - by_attendance:   theo số buổi tham dự.
 *  - by_weight:       theo trọng số tuỳ biến (weight).
 *  - exempt:          miễn (một số người share = 0).
 *  - fixed_plus_even: một phần cố định + phần còn lại chia đều.
 *  - custom:          nhập tay từng suất (engine không tự tính).
 */
export const SPLIT_RULES = [
  "equal",
  "ratio",
  "shares",
  "by_night",
  "by_attendance",
  "by_weight",
  "exempt",
  "fixed_plus_even",
  "custom",
] as const;

export const splitRuleSchema = z.enum(SPLIT_RULES);
export type SplitRule = z.infer<typeof splitRuleSchema>;

/** Tiền suất chia = chuỗi số nguyên minor-units (BigInt qua JSON), KHÔNG âm, no-float (B1). */
const SHARE_MINOR_STRING = /^\d+$/;

/**
 * Một suất chia của expense. XOR chủ thể enforce bằng `superRefine` (mirror CHECK DB).
 * `shareMinor` string (BigInt); `weight` number (input tính, KHÔNG tiền).
 */
export const expenseParticipantSchema = z
  .object({
    groupMemberId: z.string().uuid().optional(),
    dependentId: z.string().uuid().optional(),
    shareMinor: z
      .string()
      .regex(SHARE_MINOR_STRING, "shareMinor phải là chuỗi số nguyên không âm (minor units, no-float)"),
    weight: z.number().nonnegative().optional(),
    isResidualHolder: z.boolean().default(false),
  })
  .strict()
  .superRefine((val, ctx) => {
    const hasMember = val.groupMemberId != null;
    const hasDependent = val.dependentId != null;
    // XOR: đúng MỘT chủ thể (mirror expense_participants_subject_xor_check).
    if (hasMember === hasDependent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "đúng MỘT trong groupMemberId / dependentId (XOR chủ thể)",
        path: hasMember ? ["dependentId"] : ["groupMemberId"],
      });
    }
  });

export type ExpenseParticipantInput = z.infer<typeof expenseParticipantSchema>;
